import React from 'react';

import isEmpty from 'lodash/isEmpty';
import noop from 'lodash/noop';
import { AutoSizer } from 'react-virtualized';

import EmptyState from './EmptyState';
import ErrorNode from './ErrorNode';
import LoaderNode from './LoaderNode';
import { StyledList } from './styles';
import * as TreeHelper from './treeHelper';
import TreeNode from './TreeNode';
import { KeyType, NodeType, TreeRefType, VirtualizedTreeProps, VirtualizedTreeState } from './types';

/**
 * VirtualizedTree.
 * Built based on react-virtualized List.
 * https://github.com/bvaughn/react-virtualized/blob/master/docs/List.md
 */
export class VirtualizedTree extends React.Component<VirtualizedTreeProps, VirtualizedTreeState> {
  private _lastSelectedKey: KeyType | null;
  public treeListRef?: TreeRefType;
  public autoSizerRef?: any;

  public static defaultProps = {
    valueKey: 'id',
    childrenKey: 'children',
    nodes: [],
    highlightSelected: false,
    matchAlloyStyle: false,
    showSkeletonLoading: false,
    allowReselectNodes: false,
    showDisabledSelection: false,
    enableShiftSelection: false,
    enableCrossParentSelection: false,
    autoCollapseSubNodes: false,
    rowHeight: 35,
    isNodeDisabled: () => false,
    isNodeSelectable: () => true,
    editItemRenderer: noop,
    onSelectNodesChange: noop,
    onContextMenu: noop,
    onExpandNode: noop,
    onScroll: noop,
    onDuplicatedKeys: noop,
    getNodeStyle: noop,
    scrollToAlignment: 'center'
  };

  constructor(props: VirtualizedTreeProps) {
    super(props);

    this.state = {
      selectedKeys: TreeHelper.cloneArray(
        props.selectedKeys !== undefined ? props.selectedKeys : props.defaultSelectedKeys
      ),
      expandedKeys: TreeHelper.cloneArray(
        props.expandedKeys !== undefined ? props.expandedKeys : props.defaultExpandedKeys
      ),
      isMultiple:
        props.multiple === true ||
        // Auto detect multiple mode only if multiple is not defined
        (props.multiple === undefined &&
          Array.isArray(props.defaultSelectedKeys) &&
          props.defaultSelectedKeys.length > 1),
      innerState: {},
      flatNodes: []
    };

    // Save the last selected key for shift selection under multiple mode.
    this._lastSelectedKey = null;

    this.rowRenderer = this.rowRenderer.bind(this);
    this.rowHeight = this.rowHeight.bind(this);
    this.handleLoadNode = this.handleLoadNode.bind(this);
    this.handleSelectNode = this.handleSelectNode.bind(this);
    this.handleToggleNode = this.handleToggleNode.bind(this);
  }

  generateFlatNodes(expandedKeys: KeyType[], innerState: Record<string, any>, nodes: NodeType[]) {
    const { valueKey, childrenKey, onDuplicatedKeys } = this.props;
    const { flatNodes, duplicatedKeys } = TreeHelper.genFlatNodes({
      nodes,
      valueKey,
      childrenKey,
      expandedKeys,
      innerState
    });
    onDuplicatedKeys?.({ keys: duplicatedKeys, nodes: flatNodes });
    return flatNodes;
  }

  componentDidMount() {
    const { expandedKeys, innerState } = this.state;
    const { nodes } = this.props;

    this.setState({
      flatNodes: this.generateFlatNodes(expandedKeys, innerState, nodes)
    });
  }

  componentDidUpdate(preProps: VirtualizedTreeProps, preState: VirtualizedTreeState) {
    const { expandedKeys, selectedKeys, nodes } = this.props;
    const { innerState } = this.state;

    const newState = {} as VirtualizedTreeState;

    if (expandedKeys !== undefined && expandedKeys !== preProps.expandedKeys) {
      newState.expandedKeys = expandedKeys;
    }

    if (selectedKeys !== undefined && selectedKeys !== preProps.selectedKeys) {
      newState.selectedKeys = selectedKeys;
    }

    if (
      nodes !== preProps.nodes ||
      innerState !== preState.innerState ||
      (newState.expandedKeys || this.state.expandedKeys) !== preState.expandedKeys ||
      (newState.selectedKeys || this.state.selectedKeys) !== preState.selectedKeys
    ) {
      newState.flatNodes = this.generateFlatNodes(newState.expandedKeys || this.state.expandedKeys, innerState, nodes);
    }

    if (!isEmpty(newState)) {
      this.setState(newState, () => this.maybeMeasureAllRows());
    }
  }

  getExpandedKeys() {
    return TreeHelper.cloneArray(this.state.expandedKeys);
  }

  setExpandedKeys(expandedKeys: KeyType[]) {
    if (this.props.expandedKeys !== undefined) return;
    this.setState({
      expandedKeys: TreeHelper.cloneArray(expandedKeys)
    });
  }

  getSelectedKeys() {
    return TreeHelper.cloneArray(this.state.selectedKeys);
  }

  setSelectedKeys(selectedKeys: KeyType[]) {
    if (this.props.selectedKeys !== undefined) return;
    this.setState({
      selectedKeys: TreeHelper.cloneArray(selectedKeys)
    });
  }

  getFlatNodes() {
    return TreeHelper.cloneArray(this.state.flatNodes);
  }

  scrollToNode(node: NodeType | null | undefined) {
    const { valueKey } = this.props;
    const { flatNodes } = this.state;
    const index = flatNodes.findIndex((flat) => flat[valueKey] === node?.[valueKey]);
    if (index !== -1 && this.treeListRef) {
      this.treeListRef.scrollToRow(index);
    }
  }

  forceUpdate() {
    if (this.treeListRef) this.treeListRef.forceUpdateGrid();
  }

  maybeMeasureAllRows() {
    // Only retry to update if rowHeight is a function (dynamic row height)
    if (typeof this.props.rowHeight !== 'function' || !this.treeListRef) return;
    this.treeListRef.recomputeRowHeights();
  }

  handleLoadNode(node: NodeType) {
    const { valueKey, onExpandNode } = this.props;
    const { expandedKeys } = this.state;
    const nodeKey = node[valueKey];

    this.setState({
      expandedKeys: [...expandedKeys, nodeKey]
    });

    const result = onExpandNode?.(node);

    if (!(result instanceof Promise)) {
      return Promise.resolve();
    }

    this.setState({
      innerState: {
        ...this.state.innerState,
        [nodeKey]: {
          [TreeHelper.NODE_KEYS.NODE_ACTION_TYPE]: TreeHelper.NODE_KEYS.NODE_LOADER_KEY,
          parentNode: node
        }
      }
    });

    return result
      .then(() => {
        const innerState = { ...this.state.innerState };
        delete innerState[nodeKey];
        this.setState({
          innerState
        });
      })
      .catch(() => {
        this.setState({
          innerState: {
            ...this.state.innerState,
            [nodeKey]: {
              [TreeHelper.NODE_KEYS.NODE_ACTION_TYPE]: TreeHelper.NODE_KEYS.NODE_ERROR_KEY,
              parentNode: node
            }
          }
        });
      });
  }

  handleExpandNode(node: NodeType) {
    const { valueKey } = this.props;
    const { expandedKeys } = this.state;
    if (expandedKeys.includes(node[valueKey])) return Promise.resolve();
    return this.handleLoadNode(node);
  }

  handleToggleNode(node: NodeType) {
    const { valueKey, autoCollapseSubNodes, onUnexpandNode } = this.props;
    const { expandedKeys, flatNodes } = this.state;

    const nodeKey = node[valueKey];

    if (expandedKeys.includes(nodeKey)) {
      if (autoCollapseSubNodes) {
        // All sub nodes
        const subNodesKeys = flatNodes
          .filter((flat) => flat.innerPath.startsWith(node?.innerPath + '/'))
          .map((sub) => sub[valueKey]);
        // Auto unexpand all sub nodes
        this.setState({
          expandedKeys: expandedKeys.filter((key) => key !== nodeKey && !subNodesKeys.includes(key))
        });
      } else {
        this.setState({
          expandedKeys: expandedKeys.filter((key) => key !== nodeKey)
        });
      }

      onUnexpandNode?.(node);
    } else {
      this.handleExpandNode(node);
    }
  }

  handleSelectNode(node: NodeType, event: React.MouseEvent) {
    const { isMultiple, selectedKeys, flatNodes } = this.state;
    const {
      valueKey,
      isNodeDisabled,
      isNodeSelectable,
      enableShiftSelection,
      enableCrossParentSelection,
      onSelectNodesChange
    } = this.props;
    const nodeKey = node[valueKey];
    let updatedKeys = [nodeKey];

    // Handle multiple selection
    if (isMultiple) {
      const lastSelectedKey = this._lastSelectedKey;
      this._lastSelectedKey = nodeKey;

      let targetSelectKeys = [nodeKey];
      if (enableShiftSelection && event?.nativeEvent?.shiftKey === true) {
        // Clear document selection when shift key is pressed.
        document?.getSelection()?.removeAllRanges();

        // Pick selectable keys in range based on current & previous selected key.
        if (lastSelectedKey !== null && lastSelectedKey !== nodeKey) {
          targetSelectKeys = TreeHelper.getSelectableKeys({
            flatNodes,
            valueKey,
            preNodeKey: lastSelectedKey,
            curNodeKey: nodeKey,
            allowCrossParent: !!enableCrossParentSelection,
            isNodeDisabled,
            isNodeSelectable
          });
        }
      }

      // Reset updatedKeys by mutate current selectedKeys based on targetSelectKeys
      updatedKeys = [...selectedKeys];
      // Decide the batch selection behavior based on current node status.
      const isToSelect = !selectedKeys.includes(nodeKey);
      targetSelectKeys.forEach((key) => {
        if (isToSelect) {
          if (!updatedKeys.includes(key)) updatedKeys.push(key);
        } else {
          const idx = updatedKeys.indexOf(key);
          if (idx !== -1) updatedKeys.splice(idx, 1);
        }
      });
    }

    this.setState({
      selectedKeys: updatedKeys
    });

    onSelectNodesChange?.(updatedKeys, event);
  }

  isNodeExpandable(node: NodeType) {
    const { isNodeExpandable, childrenKey } = this.props;

    if (isNodeExpandable) return isNodeExpandable(node);
    return TreeHelper.hasChildren(node[childrenKey]);
  }

  rowHeight({ index }: { index: number }) {
    const { rowHeight } = this.props;
    if (typeof rowHeight !== 'function') return rowHeight;

    const node = this.state.flatNodes[index];
    return rowHeight({ index, node });
  }

  rowRenderer({
    index,
    key,
    style
  }: {
    index: number;
    key: string;
    style: React.CSSProperties & {
      height: number;
    };
  }) {
    const {
      viewItemRenderer,
      editItemRenderer,
      isNodeDisabled,
      isNodeSelectable,
      onContextMenu,
      valueKey,
      expandNodeFailureText,
      retryButtonText,
      highlightSelected,
      matchAlloyStyle,
      showSkeletonLoading,
      allowReselectNodes,
      showDisabledSelection,
      getNodeStyle,
      fixed
    } = this.props;

    const { flatNodes, selectedKeys, expandedKeys, isMultiple } = this.state;
    const node = flatNodes[index];
    // Node status
    const selected = selectedKeys.includes(node[valueKey]);
    const disabled = TreeHelper.isDisabled(node, isNodeDisabled);

    return (
      <TreeNode
        data-testid="node-label"
        aria-selected={selected ? 'true' : 'false'}
        key={`${node[valueKey]}-${key}`}
        style={{
          ...style,
          ...getNodeStyle?.({ node, selected, disabled }),
          width: fixed ? 'unset' : '100%',
          paddingLeft: node[TreeHelper.NODE_KEYS.NODE_LEVEL_KEY] * 28 + 'px'
        }}
        treeListRef={this.treeListRef}
        node={node}
        highlight={!!highlightSelected && !isMultiple}
        multiple={isMultiple}
        height={style.height}
        expandable={this.isNodeExpandable(node)}
        expanded={expandedKeys.includes(node[valueKey])}
        selectable={isNodeSelectable(node)}
        selected={selected}
        disabled={disabled}
        isActionNode={TreeHelper.isActionNode(node)}
        expandNodeFailureText={expandNodeFailureText}
        retryButtonText={retryButtonText}
        viewItemRenderer={viewItemRenderer}
        editItemRenderer={editItemRenderer}
        onReload={this.handleLoadNode}
        onToggle={this.handleToggleNode}
        onSelect={this.handleSelectNode}
        onContextMenu={onContextMenu}
        matchAlloyStyle={!!matchAlloyStyle}
        showSkeletonLoading={!!showSkeletonLoading}
        allowReselectNodes={!!allowReselectNodes}
        showDisabledSelection={!!showDisabledSelection}
      />
    );
  }

  renderTree(width: number, height: number) {
    const { flatNodes } = this.state;
    const { className, onScroll, scrollToAlignment, scrollTop, fixed } = this.props;
    // Calculate the flat nodes whenever the state changed.
    return (
      <StyledList
        aria-label="tree-list"
        ref={(node: any) => {
          this.treeListRef = node as TreeRefType;
        }}
        className={className}
        rowRenderer={this.rowRenderer}
        overscanRowCount={5}
        width={width}
        height={height}
        rowHeight={this.rowHeight}
        rowCount={flatNodes.length}
        items={flatNodes}
        onScroll={onScroll}
        scrollToAlignment={scrollToAlignment}
        scrollTop={scrollTop}
        containerStyle={fixed && { overflow: 'unset' }}
        style={fixed && { overflowX: 'auto' }}
      />
    );
  }

  render() {
    const { nodes, emptyResultText } = this.props;

    if (nodes.length === 0) return <EmptyState title={emptyResultText} />;
    return (
      <AutoSizer ref={(node: any) => (this.autoSizerRef = node)}>
        {({ width, height: offsetHeight }: { width: number; height: number }) => {
          // AutoSizer will return height with integer (offsetHeight) instead of float for
          //   🖼️ Pixel Boundaries: Final rendering must align to discrete screen pixels
          //   ⚡ Performance: Integer operations are significantly faster than floating-point math
          //   📚 Historical Design: Early web APIs prioritized speed over sub-pixel precision
          //   🔧 Browser Optimization: Layout engines are optimized for integer calculations
          //   💾 Memory Efficiency: Integers use less memory and avoid floating-point precision issues
          // Here we switch to use getBoundingClientRect to return precise positioning.
          //   It can help ensure setup tree height no larger than the parent node.
          //   It will avoid the scrollbar appear unexpectedly. Especially when zooming in / out.
          //   E.g. offsetHeight = 100, precise height = 99.96 can cause the scrollbar to appear.
          //   Check https://jira.autodesk.com/browse/ACSD-45159 for sample.
          const parentNode = this.autoSizerRef?._autoSizer?.parentNode;
          const height = parentNode?.getBoundingClientRect?.().height || offsetHeight;
          return this.renderTree(width, height);
        }}
      </AutoSizer>
    );
  }
}

export default Object.assign(VirtualizedTree, {
  NODE_KEYS: TreeHelper.NODE_KEYS,
  TreeNode,
  LoaderNode,
  ErrorNode
});
